import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { ApiClient, Client, Contractor } from '@fixterconnect/core';
import { API_BASE_URL } from '../config/api';

type ContractorUser = Contractor & { type: 'contractor' };
type ClientUser = Client & { type: 'client' };
type User = ContractorUser | ClientUser;

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  userType: 'contractor' | 'client' | null;
  login: (email: string, password: string) => Promise<User | null>;
  loginWithGoogle: (credential: string, accountType?: 'client' | 'contractor') => Promise<{ user: User | null; isNewUser: boolean }>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

// Create the context with a default undefined value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create a provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userType, setUserType] = useState<'contractor' | 'client' | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const apiClient = new ApiClient(API_BASE_URL);

  // Check for existing session on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Try server-side session check first (httpOnly cookie)
        const res = await fetch(`${API_BASE_URL}/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.user) {
            const restoredUser: User = { ...data.user, type: data.userType };
            setUser(restoredUser);
            setUserType(data.userType);
            // Sync to localStorage for offline/transition reference
            localStorage.setItem('user', JSON.stringify(restoredUser));
            localStorage.setItem('userType', data.userType);
            return;
          }
        }

        // Fallback: check localStorage (transition period — Bearer token still works)
        const savedUser = localStorage.getItem('user');
        const savedUserType = localStorage.getItem('userType');
        const savedToken = localStorage.getItem('authToken');

        if (savedUser && savedUserType && savedToken) {
          setUser(JSON.parse(savedUser));
          setUserType(savedUserType as 'contractor' | 'client');
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        // Network error on /me — fall back to localStorage
        try {
          const savedUser = localStorage.getItem('user');
          const savedUserType = localStorage.getItem('userType');
          const savedToken = localStorage.getItem('authToken');
          if (savedUser && savedUserType && savedToken) {
            setUser(JSON.parse(savedUser));
            setUserType(savedUserType as 'contractor' | 'client');
          }
        } catch {
          localStorage.removeItem('user');
          localStorage.removeItem('userType');
          localStorage.removeItem('authToken');
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<User | null> => {
    setIsLoading(true);

    try {
      // Try logging in as contractor first
      const contractorResponse = await apiClient.login(email, password, 'contractor');

      if (contractorResponse.success && contractorResponse.contractor) {
        const loggedInUser: User = { ...contractorResponse.contractor, type: 'contractor' as const };
        setUser(loggedInUser);
        setUserType('contractor');
        localStorage.setItem('user', JSON.stringify(loggedInUser));
        localStorage.setItem('userType', 'contractor');
        if ((contractorResponse as any).token) {
          localStorage.setItem('authToken', (contractorResponse as any).token);
        }
        return loggedInUser;
      }

      // If contractor login fails, try client login
      const clientResponse = await apiClient.login(email, password, 'client');

      if (clientResponse.success && clientResponse.client) {
        const loggedInUser: User = { ...clientResponse.client, type: 'client' as const };
        setUser(loggedInUser);
        setUserType('client');
        localStorage.setItem('user', JSON.stringify(loggedInUser));
        localStorage.setItem('userType', 'client');
        if ((clientResponse as any).token) {
          localStorage.setItem('authToken', (clientResponse as any).token);
        }
        return loggedInUser;
      }

      return null;
    } catch (error) {
      console.error('Login error:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async (credential: string, accountType?: 'client' | 'contractor'): Promise<{ user: User | null; isNewUser: boolean }> => {
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ credential, accountType })
      });

      const data = await response.json();

      if (data.success && data.user) {
        const loggedInUser: User = {
          ...data.user,
          type: data.userType as 'contractor' | 'client'
        };
        setUser(loggedInUser);
        setUserType(data.userType);
        localStorage.setItem('user', JSON.stringify(loggedInUser));
        localStorage.setItem('userType', data.userType);
        if (data.token) {
          localStorage.setItem('authToken', data.token);
        }
        return { user: loggedInUser, isNewUser: data.isNewUser };
      }

      return { user: null, isNewUser: false };
    } catch (error) {
      console.error('Google login error:', error);
      return { user: null, isNewUser: false };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // Clear server-side cookie
    try {
      await fetch(`${API_BASE_URL}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Best-effort — clear local state regardless
    }
    setUser(null);
    setUserType(null);
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    localStorage.removeItem('authToken');
  };

  // Create the value object
  const value = {
    isAuthenticated: !!user,
    user,
    userType,
    login,
    loginWithGoogle,
    logout,
    isLoading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};